/**
 * Controlled address field with Mapbox-backed autocomplete.
 *
 * The flow mirrors a lightweight onboarding-style address capture: the user
 * types into a single "street" input (the typeahead), picks a match (or the
 * always-present "Add … manually" escape hatch), and then confirms the
 * broken-out fields. Picking a suggestion parses it into address/city/state/zip
 * and reveals those fields for a final edit (e.g. appending a suite number in
 * the Address line — there is intentionally no separate line-2 field here).
 *
 * Purely controlled: every field edit and every pick calls `onChange(next)`.
 * The host owns Save/Cancel, so `onCommit` is optional and unused by the
 * finance editor today — it exists for a future host that wants an explicit
 * in-component confirm affordance.
 */
import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete';
import { parseMapboxAddress, type MapboxFeature } from '@/lib/mapbox/geocoding';
import { US_STATE_OPTIONS } from '@/lib/usStates';
import { cn } from '@/lib/utils';
import { financeFormInputClass } from '@/components/finance/shared/financeControlClasses';

export interface AddressAutocompleteValue {
  address: string;
  /** Optional second postal line (suite/apt). Never populated by a Mapbox pick;
   *  edited by hand in the revealed fields. Empty string when unset. */
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * The structured, Mapbox-canonicalized address captured when the user PICKS a
 * suggestion. It is the SAME shape as the filing value at pick time, but it is
 * tracked separately: it survives only while the visible filing address still
 * matches the pick. A hand-typed / "add manually" address, or any post-pick edit
 * to the revealed fields, means there is no canonical value (emit null). This
 * feeds the dedup waterfall's tier-3 canonical match on the create/update request
 * and NEVER overwrites the filing address.
 */
export interface CanonicalAddressValue {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressAutocompleteProps {
  value: AddressAutocompleteValue;
  onChange: (next: AddressAutocompleteValue) => void;
  onCommit?: (next: AddressAutocompleteValue) => void;
  /**
   * Optional: emitted alongside `onChange`. A `CanonicalAddressValue` when the
   * user just picked a Mapbox suggestion; `null` on a manual entry or any later
   * hand-edit to the revealed fields. Callers wire the value into the request's
   * `canonicalAddress*` fields (omit when null).
   */
  onCanonicalChange?: (canonical: CanonicalAddressValue | null) => void;
  disabled?: boolean;
  idPrefix?: string;
  /**
   * Where the Mapbox token comes from. Default `'authenticated'` (admin
   * surfaces — unchanged for every existing caller). The unauthenticated donor
   * page passes `'public'` so ONLY the restricted public token is fetched
   * (never the admin token).
   */
  tokenSource?: 'authenticated' | 'public';
  /** Subsidiary code for the `'public'` token route; ignored when authenticated. */
  tokenSubsidiary?: string;
  /**
   * Render the broken-out Address/City/State/ZIP fields ALONGSIDE the search box,
   * instead of hiding them until the user commits via the dropdown.
   *
   * Set by the public donation page, where the address is required and the dropdown
   * cannot be relied on: the embed iframe is `overflow:hidden` / `scrolling="no"` and
   * sized from a posted `scrollHeight` that an absolutely-positioned dropdown never
   * changes, so on a phone the dropdown opens into a clipped region the donor cannot
   * reach. With the dropdown being the only route to these fields, that made the gift
   * uncompletable. Off by default, so admin surfaces keep the compact behaviour.
   */
  alwaysShowFields?: boolean;
}

export function AddressAutocomplete({
  value,
  onChange,
  onCommit: _onCommit,
  onCanonicalChange,
  disabled = false,
  idPrefix = 'address-autocomplete',
  tokenSource = 'authenticated',
  tokenSubsidiary,
  alwaysShowFields = false,
}: AddressAutocompleteProps) {
  // Show the broken-out fields once the user has committed to an address —
  // either by picking a suggestion / "manually", or because the host handed us
  // a partial address to edit.
  const [revealed, setRevealed] = useState(
    () => Boolean(value.address || value.city || value.state || value.zip),
  );

  const { query, setQuery, suggestions, loading, clear, tokenError } =
    useAddressAutocomplete({ enabled: !disabled, tokenSource, tokenSubsidiary });

  const inputId = `${idPrefix}-search`;

  const selectSuggestion = (feature: MapboxFeature) => {
    const parsed = parseMapboxAddress(feature);
    // A Mapbox pick never fills line 2 — preserve whatever the user typed there.
    onChange({
      address: parsed.streetAddress,
      addressLine2: value.addressLine2,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
    });
    // The picked structured value IS the canonical address (line 2 is a hand
    // field Mapbox never fills, so carry the existing line 2).
    onCanonicalChange?.({
      line1: parsed.streetAddress,
      line2: value.addressLine2,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
    });
    setRevealed(true);
    clear();
  };

  const selectManual = () => {
    // Escape hatch: keep whatever the user typed as the street line (and any
    // line 2 already entered), blank the rest, and let them fill it in by hand.
    onChange({ address: query, addressLine2: value.addressLine2, city: '', state: '', zip: '' });
    // A hand-typed address is not canonical.
    onCanonicalChange?.(null);
    setRevealed(true);
    clear();
  };

  const updateField = (patch: Partial<AddressAutocompleteValue>) => {
    onChange({ ...value, ...patch });
    // Any hand-edit to the revealed fields means the visible/filing address has
    // diverged from the structured pick — drop canonical so it can't feed a
    // false tier-3 match.
    onCanonicalChange?.(null);
  };

  const dropdownOpen = !revealed && query.length > 0;

  // With alwaysShowFields the search box and the broken-out fields coexist: the autocomplete stays a
  // convenience, but it is never the only way to enter an address. Everywhere else they remain
  // mutually exclusive, exactly as before.
  const showSearch = !revealed || alwaysShowFields;
  const showFields = revealed || alwaysShowFields;

  return (
    <div className="space-y-2">
      {showSearch && (
        <div className="relative">
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id={inputId}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={disabled}
              aria-label="Registered address"
              placeholder="Start typing an address…"
              autoComplete="off"
              className={cn(financeFormInputClass, 'w-full pl-8')}
            />
          </div>

          {dropdownOpen && (
            <ul
              role="listbox"
              className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md"
            >
              {suggestions.map((feature) => {
                const parsed = parseMapboxAddress(feature);
                return (
                  <li key={feature.id}>
                    <button
                      type="button"
                      onClick={() => selectSuggestion(feature)}
                      className="flex w-full items-start gap-2 px-2 py-2 text-left hover:bg-accent"
                    >
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold">
                          {parsed.streetAddress || feature.text}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {feature.place_name}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}

              {/* Always-present escape hatch — use exactly what was typed. */}
              <li>
                <button
                  type="button"
                  onClick={selectManual}
                  className="flex w-full items-center gap-2 border-t px-2 py-2 text-left text-sm hover:bg-accent"
                >
                  <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    Add "{query}" manually
                  </span>
                </button>
              </li>
            </ul>
          )}

          {loading && (
            <p className="mt-1 text-xs text-muted-foreground">Searching…</p>
          )}
          {tokenError && (
            <p className="mt-1 text-xs text-muted-foreground">
              Address lookup is unavailable. You can still enter an address by hand.
            </p>
          )}
        </div>
      )}

      {showFields && (
        <div className="space-y-2">
          <div>
            <label
              htmlFor={`${idPrefix}-address`}
              className="block text-xs font-medium mb-0.5"
            >
              Address
            </label>
            <input
              id={`${idPrefix}-address`}
              type="text"
              value={value.address}
              onChange={(e) => updateField({ address: e.target.value })}
              disabled={disabled}
              className={cn(financeFormInputClass, 'w-full')}
            />
          </div>
          <div>
            <label
              htmlFor={`${idPrefix}-address2`}
              className="block text-xs font-medium mb-0.5"
            >
              Address line 2
            </label>
            <input
              id={`${idPrefix}-address2`}
              data-testid={`${idPrefix}-address2`}
              type="text"
              value={value.addressLine2}
              onChange={(e) => updateField({ addressLine2: e.target.value })}
              disabled={disabled}
              placeholder="Apt, suite, floor (optional)"
              className={cn(financeFormInputClass, 'w-full')}
            />
          </div>
          <div className="grid grid-cols-3 gap-1">
            <div>
              <label
                htmlFor={`${idPrefix}-city`}
                className="block text-xs font-medium mb-0.5"
              >
                City
              </label>
              <input
                id={`${idPrefix}-city`}
                type="text"
                value={value.city}
                onChange={(e) => updateField({ city: e.target.value })}
                disabled={disabled}
                className={cn(financeFormInputClass, 'w-full')}
              />
            </div>
            <div>
              <label
                htmlFor={`${idPrefix}-state`}
                className="block text-xs font-medium mb-0.5"
              >
                State
              </label>
              <select
                id={`${idPrefix}-state`}
                value={value.state}
                onChange={(e) => updateField({ state: e.target.value })}
                disabled={disabled}
                className={cn(financeFormInputClass, 'w-full')}
              >
                {US_STATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.value ? opt.value : opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor={`${idPrefix}-zip`}
                className="block text-xs font-medium mb-0.5"
              >
                Zip
              </label>
              <input
                id={`${idPrefix}-zip`}
                type="text"
                value={value.zip}
                onChange={(e) => updateField({ zip: e.target.value })}
                disabled={disabled}
                className={cn(financeFormInputClass, 'w-full')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
